import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { addTransportConditionCheckedItem, deleteTransportConditionCheckedItem, setTransportConditionCheckedItem } from 'app/actions/map';
import { Scrollbar } from 'app/components';
import { useLocalStorage } from 'app/services';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const mapTransportConditionFilterFullList = () => {
  const dispatch = useDispatch();
  const transportSelectedItem = useSelector((state) => state.map.transportSelectedItem);
  const checkedItems = useSelector((state) => state.map.transportConditionFilterCheckedItems);
  const [, setCheckedStorageItems] = useLocalStorage('transportConditionFilterCheckedItems', []);

  const conditionFilters = useMemo(() => {
    return config.mapTransportConditionFilters
      .filter((eventFilter) => eventFilter.deviceTypes.includes(config.transportDeviceTypes[transportSelectedItem?.device_type]))
      .map((eventFilter) => eventFilter.filters)
      .reduce((accumulator, currentValue) => [...accumulator, ...currentValue], []);
  }, [transportSelectedItem]);

  const events = useMemo(() => {
    return Object.values(config.compareEvents)
      .map((event) => event)
      .reduce((accumulator, currentValue) => [...accumulator, ...currentValue], []);
  }, []);

  const handleResetClick = () =>
    dispatch(setTransportConditionCheckedItem(checkedItems.length === conditionFilters.length ? [] : conditionFilters));

  const handleAnalysisClick = (event) =>
    dispatch(setTransportConditionCheckedItem(event.target.checked ? config.mapTransportConditionAnalysisFilters : conditionFilters));

  const handleHeadClick = (event, filters) => {
    event.preventDefault();
    event.stopPropagation();

    const filterItems = filters.filter((filter) => checkedItems.indexOf(filter) === -1);
    const checkedFilters = checkedItems.filter((eventFilter) => filters.indexOf(eventFilter) === -1);

    dispatch(filterItems.length !== 0 ? addTransportConditionCheckedItem(filterItems) : setTransportConditionCheckedItem(checkedFilters));
  };

  const handleItemClick = (value) =>
    dispatch(checkedItems.indexOf(value) !== -1 ? deleteTransportConditionCheckedItem([value]) : addTransportConditionCheckedItem([value]));

  useEffect(() => {
    setCheckedStorageItems(checkedItems);
  }, [checkedItems]);

  return (
    <Stack
      direction='column'
      sx={(theme) => ({
        height: '100%',
        '.simplebar-content-wrapper': {
          padding: theme.spacing(0),
        },
        '.MuiAccordion-root': {
          borderRadius: '0',
          overflow: 'hidden',
          userSelect: 'none',
          transition: 'background-color ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '0',
          },
          '&.Mui-disabled': {
            bgcolor: 'background.paper',
            '.MuiAccordionSummary-root': {
              opacity: '1',
            },
            '.MuiAccordionSummary-expandIconWrapper': {
              display: 'none',
            },
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            svg: {
              fontSize: '18px',
            },
          },
        },
        '.MuiAccordionSummary-root': {
          padding: theme.spacing(0, 2),
          minHeight: '24px',
          transition: 'none',
          '&.Mui-expanded': {
            minHeight: '24px',
            borderRadius: '0',
          },
        },
        '.MuiAccordionSummary-content': {
          alignItems: 'center',
          overflow: 'hidden',
          margin: theme.spacing(0),
          '&.Mui-expanded': {
            margin: theme.spacing(0),
          },
          '.MuiCheckbox-root': {
            minWidth: '26px',
            justifyContent: 'flex-start',
            padding: theme.spacing(0),
            svg: {
              fontSize: '18px',
            },
          },
        },
        '.MuiAccordionDetails-root': {
          padding: theme.spacing(0),
          '.MuiList-root': {
            margin: '2px 0',
          },
        },
      })}>
      <Scrollbar>
        <Accordion square elevation={0}>
          <AccordionSummary disableRipple>
            <Checkbox
              size='small'
              color='primary'
              name='all'
              disableRipple
              onClick={handleResetClick}
              checked={checkedItems.length === conditionFilters.length}
              indeterminate={checkedItems.length < conditionFilters.length && checkedItems.length > 0}
            />
            <Typography>Все фильтры</Typography>
          </AccordionSummary>
        </Accordion>
        {config.transportDeviceTypes[transportSelectedItem.device_type] === 'galileosky' && (
          <Accordion square elevation={0}>
            <AccordionSummary disableRipple>
              <Checkbox
                size='small'
                color='primary'
                name='all'
                disableRipple
                onClick={handleAnalysisClick}
                checked={
                  config.mapTransportConditionAnalysisFilters.every((filter) => checkedItems.includes(filter)) &&
                  checkedItems.length === config.mapTransportConditionAnalysisFilters.length
                }
              />
              <Typography>Разбор тревог</Typography>
            </AccordionSummary>
          </Accordion>
        )}
        {config.mapTransportConditionFilters
          .filter((eventFilter) => eventFilter.deviceTypes.includes(config.transportDeviceTypes[transportSelectedItem?.device_type]))
          .map((eventFilter, key) => (
            <Accordion key={key} square elevation={0} defaultExpanded TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary disableRipple expandIcon={<ExpandMore />}>
                <Checkbox
                  size='small'
                  color='primary'
                  disableRipple
                  name={eventFilter.name}
                  onClick={(event) => handleHeadClick(event, eventFilter.filters)}
                  checked={eventFilter.filters.every((filter) => checkedItems.includes(filter))}
                  indeterminate={
                    !eventFilter.filters.every((filter) => checkedItems.includes(filter)) &&
                    eventFilter.filters.reduce((accumulator, currentValue) => {
                      if (checkedItems.includes(currentValue)) {
                        return [...accumulator, ...currentValue];
                      }
                      return accumulator;
                    }, []).length !== 0
                  }
                />
                <Typography>{eventFilter.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {eventFilter.filters.map((filter, index) => (
                    <ListItem
                      key={index}
                      disablePadding
                      onClick={() => handleItemClick(filter)}
                      sx={(theme) => ({
                        alignItems: 'flex-start',
                        padding: theme.spacing(0.5, 1),
                        paddingLeft: '36px',
                        borderRadius: '0',
                        '&:hover': {
                          cursor: 'pointer',
                        },
                        '.MuiListItemIcon-root': {
                          minWidth: '26px',
                          '.MuiCheckbox-root': {
                            padding: '0',
                            minWidth: '26px',
                            justifyContent: 'flex-start',
                            svg: {
                              fontSize: '18px',
                            },
                          },
                        },
                        '.MuiListItemText-root': {
                          margin: theme.spacing(0),
                        },
                      })}>
                      <ListItemIcon>
                        <Checkbox size='small' color='primary' name={filter} disableRipple checked={checkedItems.indexOf(filter) !== -1} />
                      </ListItemIcon>
                      <ListItemText primary={events.find((event) => event.key === filter)?.message} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
      </Scrollbar>
    </Stack>
  );
};

export default mapTransportConditionFilterFullList;
